import { useState, useCallback, useEffect } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { motion } from "framer-motion";
import { Skeleton } from "../components/ui/skeleton";

interface ImageCarouselProps {
  images: Array<{
    url: string;
    alt: string;
    caption?: string;
  }>;
  className?: string;
  autoPlay?: boolean;
  autoPlayInterval?: number;
  pauseOnHover?: boolean;
}

export const ImageCarousel = ({
  images,
  className = "",
  autoPlay = true,
  autoPlayInterval = 5000,
  pauseOnHover = true,
}: ImageCarouselProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "center",
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi, setSelectedIndex]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
  }, [emblaApi, onSelect]);

  // Autoplay
  useEffect(() => {
    if (!emblaApi || !autoPlay) return;

    const autoplayInterval = setInterval(() => {
      if (!isPaused && emblaApi) {
        emblaApi.scrollNext();
      }
    }, autoPlayInterval);

    return () => clearInterval(autoplayInterval);
  }, [emblaApi, autoPlay, autoPlayInterval, isPaused]);

  if (!images || images.length === 0) return null;

  return (
    <div
      className={`relative ${className}`}
      onMouseEnter={() => pauseOnHover && setIsPaused(true)}
      onMouseLeave={() => pauseOnHover && setIsPaused(false)}
    >
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {images.map((image, index) => (
            <div key={index} className="flex-[0_0_100%] min-w-0 relative">
              <div className="relative aspect-[16/11] xl:aspect-[16/7] lg:aspect-[16/10] md:aspect-[16/10] sm:aspect-[16/10] overflow-hidden rounded-xl shadow-divine">
                <img
                  src={image.url}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-all duration-700 hover:scale-105"
                  loading={index === 0 ? "eager" : "lazy"}
                  fetchPriority={index === 0 ? "high" : "auto"}
                  decoding="async"
                />
                {image.caption && (
                  <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/60 to-transparent">
                    <p className="text-white text-lg font-medium drop-shadow-lg">{image.caption}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Buttons */}
      <Button
        variant="ghost"
        size="icon"
        className="absolute left-4 top-1/2 -translate-y-1/2 bg-gray-600/80 hover:bg-gray-700 hover:text-white text-white rounded-full backdrop-blur-sm z-10 shadow-lg transition-all duration-300"
        onClick={scrollPrev}
        data-testid="carousel-prev"
      >
        <ChevronLeft className="h-5 w-5" />
      </Button>

      <Button
        variant="ghost"
        size="icon"
        className="absolute right-4 top-1/2 -translate-y-1/2 bg-gray-600/80 hover:bg-gray-700 hover:text-white text-white rounded-full backdrop-blur-sm z-10 shadow-lg transition-all duration-300"
        onClick={scrollNext}
        data-testid="carousel-next"
      >
        <ChevronRight className="h-5 w-5" />
      </Button>

      {/* Dots Indicator */}
      <div className="flex justify-center gap-2 mt-6">
        {images.map((_, index) => (
          <button
            key={index}
            className={`w-3 h-3 rounded-full transition-all duration-300 shadow-sm ${
              index === selectedIndex
                ? "bg-gradient-to-r from-ministry-gold to-ministry-purple scale-125 shadow-divine"
                : "bg-gray-300 hover:bg-gradient-to-r hover:from-ministry-cyan hover:to-ministry-emerald"
            }`}
            onClick={() => emblaApi?.scrollTo(index)}
            data-testid={`carousel-dot-${index}`}
          />
        ))}
      </div>
    </div>
  );
};
